/**
 * 2段階認証の設定の場所（プロフィール › セキュリティの節）。**場所はここ1か所だけで決める。**
 *
 * 決定（2026-10-07）：登録・確認・バックアップコードの表示・無効化は、別の画面ではなく
 * プロフィール（アカウントの設定）の「セキュリティ」の節で、画面を移らずに行う。
 * プロフィールは社員用の Layout の上に開くダイアログなので、URL の `?account=security` で開く。
 *   - 今の画面のまま開く：mfaSettingsHrefOn(pathname)（帯・入口のリンク）
 *   - 行き先の画面が無いとき（必須の会社の誘導）：MFA_SETTINGS_HREF
 *   - G-PLAN 限定の人：G_PLAN_MFA_SETTINGS_HREF（G-PLAN の画面の上で同じ節を開く）
 *   - ログインの直後：afterLoginDestination（必須の人は戻り先より先に上の2つへ）
 */

/** プロフィールのどの節を開くかの URL のキーと値。 */
export const ACCOUNT_SECTION_PARAM = 'account'
export const ACCOUNT_SECURITY_SECTION = 'security'

/** プロフィールの中の「セキュリティ」の節の id（開いたときにここへ送る）。 */
export const ACCOUNT_SECURITY_SECTION_ID = 'account-security'

/**
 * 必須の会社で未登録の人を誘導する先（データをほとんど取りに行かない使い方の画面 /guide の上でプロフィールのセキュリティを開く）。
 * /bt-progress の上だと、登録するまで 403 になる進捗管理表が窓の後ろでエラーの帯2本を出すため替えた（AUTH-01 補足6・2026-10-30）。
 */
export const MFA_SETTINGS_HREF = `/guide?${ACCOUNT_SECTION_PARAM}=${ACCOUNT_SECURITY_SECTION}`

/** G-PLAN 限定の人の誘導先（ミドルウェアが /bt-progress を通さないため G-PLAN の画面の上で開く）。 */
export const G_PLAN_MFA_SETTINGS_HREF = `/g-plan?${ACCOUNT_SECTION_PARAM}=${ACCOUNT_SECURITY_SECTION}`

/**
 * ログインの直後の行き先（AUTH-01・2026-10-30）。必須の人は業務の画面（戻り先）を通さず登録の画面へ。
 * nextPath は安全化済みの戻り先（resolveSafeNextPath の結果）を渡す。
 */
export function afterLoginDestination(input: {
  mustEnroll: boolean
  gplanOnly: boolean
  nextPath: string
}): string {
  if (!input.mustEnroll) return input.nextPath
  return input.gplanOnly ? G_PLAN_MFA_SETTINGS_HREF : MFA_SETTINGS_HREF
}

/** 今の画面のままプロフィールのセキュリティの節を開くリンク（ほかのクエリは残す）。 */
export function mfaSettingsHrefOn(pathname: string, search = ''): string {
  const params = new URLSearchParams(search)
  params.set(ACCOUNT_SECTION_PARAM, ACCOUNT_SECURITY_SECTION)
  return `${pathname}?${params.toString()}`
}

/** URL のクエリがセキュリティの節を開く指定か。 */
export function isAccountSecurityRequested(
  search: Pick<URLSearchParams, 'get'> | null | undefined
): boolean {
  return search?.get(ACCOUNT_SECTION_PARAM) === ACCOUNT_SECURITY_SECTION
}

/** 閉じたときに URL から指定を外す（ほかのクエリは残す）。 */
export function withoutAccountSection(pathname: string, search = ''): string {
  const params = new URLSearchParams(search)
  params.delete(ACCOUNT_SECTION_PARAM)
  const rest = params.toString()
  return rest ? `${pathname}?${rest}` : pathname
}
